/**
 * 请求工具函数
 *
 * @description 提供统一的请求头处理和工具函数
 * @author Cross-Border ERP Team
 * @version 1.0.0
 */

import type { ApiRequestConfig } from '@/services/typings';

/**
 * 生成唯一的请求追踪ID
 *
 * @returns 格式为 req_timestamp_random 的请求ID
 */
export function generateRequestId(): string {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 8);
  return `req_${timestamp}_${random}`;
}

/**
 * 从localStorage获取认证token
 *
 * @returns 认证token或null
 */
export function getAuthToken(): string | null {
  try {
    // 尝试从多个可能的存储位置获取token
    const token =
      localStorage.getItem('token') ||
      localStorage.getItem('authToken') ||
      localStorage.getItem('access_token');

    if (token) {
      // 移除可能的引号包装
      return token.replace(/^["']|["']$/g, '');
    }

    return null;
  } catch (error) {
    console.warn('[Request Utils] Failed to get auth token:', error);
    return null;
  }
}

/**
 * 获取当前租户ID
 *
 * @returns 租户ID或null
 */
export function getCurrentTenantId(): string | null {
  try {
    // 尝试从多个可能的存储位置获取租户ID
    const tenantId =
      localStorage.getItem('tenantId') ||
      localStorage.getItem('tenant_id') ||
      localStorage.getItem('currentTenant');

    if (tenantId) {
      // 移除可能的引号包装
      return tenantId.replace(/^["']|["']$/g, '');
    }

    return null;
  } catch (error) {
    console.warn('[Request Utils] Failed to get tenant ID:', error);
    return null;
  }
}

/**
 * 获取API版本
 *
 * @returns API版本，默认为v1
 */
export function getApiVersion(): string {
  return 'v1';
}

/**
 * 构建统一的请求头
 *
 * @param customHeaders 自定义请求头
 * @returns 包含统一请求头的对象
 */
export function buildRequestHeaders(
  customHeaders?: Partial<ApiRequestConfig> | undefined,
): Record<string, any> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-API-Version': getApiVersion(),
    'X-Request-ID': generateRequestId(),
  };

  // 添加认证token（如果存在）
  const token = getAuthToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  // 添加租户ID（如果存在）
  const tenantId = getCurrentTenantId();
  if (tenantId) {
    headers['X-Tenant-ID'] = tenantId;
  }

  // 合并自定义头部，自定义头部优先级更高
  return { ...headers, ...customHeaders };
}

/**
 * 检查是否需要添加认证头
 *
 * @param url 请求URL
 * @returns 是否需要认证
 */
export function needsAuthentication(url: string): boolean {
  // 不需要认证的URL列表
  const publicUrls = [
    '/api/health',
    '/api/ping',
    '/api/auth/login',
    '/api/auth/register',
    '/api/auth/forgot-password',
  ];

  return !publicUrls.some((publicUrl) => url.includes(publicUrl));
}

/**
 * 日志记录请求头信息（仅开发环境）
 *
 * @param url 请求URL
 * @param headers 请求头
 */
export function logRequestHeaders(
  url: string,
  headers: Record<string, string>,
): void {
  if (process.env.NODE_ENV === 'development') {
    console.log(`[Request Headers] ${url}`, {
      Authorization: headers.Authorization ? '***Bearer Token***' : 'None',
      'X-API-Version': headers['X-API-Version'],
      'X-Tenant-ID': headers['X-Tenant-ID'] || 'None',
      'X-Request-ID': headers['X-Request-ID'],
      'Content-Type': headers['Content-Type'],
    });
  }
}
